import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, type ListInput, type ListTotal, type QueryEnvelope } from '../../api/client';
import { useAppServices } from '../ViewContext';

/**
 * Chargement d'une collection (ADR-096 §2) : première page avec compteur et totaux, puis pages
 * suivantes par curseur à la demande du défilement. Une seule requête en vol ; une réponse arrivée
 * après un changement de requête est ignorée ; une erreur de suite garde les lignes déjà là et se
 * réessaie. Le serveur revalide tout : la requête n'est que la forme gouvernée de l'Object API.
 */

export type Row = Record<string, unknown> & { id?: string; version?: number };

export interface CollectionQuery {
  type: string;
  input: Omit<ListInput, 'cursor' | 'limit' | 'include_total'>;
  pageSize: number;
  /** Tri à retirer si le serveur le refuse (ADR-092 §7) : la liste s'affiche sans tri, avec un avertissement. */
  dropSortOnError?: boolean;
}

export interface CollectionState {
  status: 'loading' | 'ready' | 'error';
  rows: Row[];
  total?: number;
  totals?: ListTotal[];
  meta?: QueryEnvelope['meta'];
  error?: { category: string; code: string; message: string };
  sortWarning: string | null;
  /** Une page suivante est en cours de chargement. */
  loadingMore: boolean;
  moreError: string | null;
  /** Plus rien à charger. */
  done: boolean;
}

const normalize = (error: unknown): { category: string; code: string; message: string } =>
  error instanceof ApiError ? error.toServiceError() : { category: 'INTERNAL_ERROR', code: 'CLIENT_ERROR', message: error instanceof Error ? error.message : String(error) };

export function useCollection(query: CollectionQuery | null, refreshKey: unknown): CollectionState & { loadMore: () => void; reload: () => void } {
  const services = useAppServices();
  const key = query === null ? 'none' : JSON.stringify({ type: query.type, input: query.input, pageSize: query.pageSize, refreshKey });
  const [state, setState] = useState<CollectionState>({ status: 'loading', rows: [], sortWarning: null, loadingMore: false, moreError: null, done: false });
  const [attempt, setAttempt] = useState(0);
  const ticket = useRef(0);
  const cursor = useRef<string | undefined>(undefined);
  const inFlight = useRef(false);
  const effectiveInput = useRef<ListInput>({});
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    const current = queryRef.current;
    const mine = ++ticket.current;
    cursor.current = undefined;
    inFlight.current = false;
    if (current === null) return;
    // La liste précédente reste visible pendant le rechargement (pas d'écran blanc).
    setState((previous) => ({ ...previous, status: 'loading', moreError: null, loadingMore: false }));
    const input: ListInput = { ...current.input, limit: current.pageSize, include_total: true };
    void (async () => {
      let first: QueryEnvelope<Row[]>;
      let sortWarning: string | null = null;
      try {
        try {
          first = await services.client.listObjects<Row>(current.type, input);
        } catch (error) {
          if (current.dropSortOnError !== true || !(error instanceof ApiError) || error.code !== 'SORT_INVALID' || input.sort === undefined) throw error;
          delete input.sort;
          first = await services.client.listObjects<Row>(current.type, input);
          sortWarning = error.message;
        }
      } catch (error) {
        if (mine === ticket.current) setState((previous) => ({ ...previous, status: 'error', error: normalize(error), loadingMore: false }));
        return;
      }
      if (mine !== ticket.current) return;
      // Les pages suivantes reprennent la requête effective, sans compteur ni totaux (ils valent pour tout l'ensemble).
      const next: ListInput = { ...input, include_total: false };
      delete next.totals;
      effectiveInput.current = next;
      cursor.current = first.page?.next_cursor;
      setState({
        status: 'ready',
        rows: [...first.data],
        ...(first.page?.total === undefined ? {} : { total: first.page.total }),
        ...(first.page?.totals === undefined ? {} : { totals: first.page.totals }),
        meta: first.meta,
        sortWarning,
        loadingMore: false,
        moreError: null,
        done: cursor.current === undefined,
      });
    })();
    // La clé résume la requête : la relancer quand elle change, ou sur « Réessayer ».
  }, [key, attempt, services.client]);

  const loadMore = useCallback(() => {
    const current = queryRef.current;
    const after = cursor.current;
    if (current === null || inFlight.current || after === undefined) return;
    const mine = ticket.current;
    inFlight.current = true;
    setState((previous) => ({ ...previous, loadingMore: true, moreError: null }));
    void (async () => {
      try {
        const page = await services.client.listObjects<Row>(current.type, { ...effectiveInput.current, cursor: after });
        if (mine !== ticket.current) return;
        cursor.current = page.page?.next_cursor;
        setState((previous) => ({ ...previous, rows: [...previous.rows, ...page.data], loadingMore: false, done: cursor.current === undefined }));
      } catch (error) {
        if (mine === ticket.current) setState((previous) => ({ ...previous, loadingMore: false, moreError: normalize(error).message }));
      } finally {
        if (mine === ticket.current) inFlight.current = false;
      }
    })();
  }, [services.client]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  return { ...state, loadMore, reload };
}
